<template>
  <div class="communication-tracking-container">
    <!-- Unified Statistics Cards - Clean Design -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Total Communications Sent -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Total Sent</p>
        <p class="text-4xl font-bold text-white">{{ communicationStats.totalCustomers }}</p>
        <div class="flex items-center gap-3 mt-2 text-xs text-gray-400">
          <span class="px-2 py-1 bg-blue-500/20 text-blue-300 rounded">
            Email: {{ communicationStats.withEmail }}
          </span>
          <span class="px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded">
            SMS: {{ communicationStats.withSMS }}
          </span>
        </div>
      </div>
      
      <!-- Delivery Rate -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Delivery Rate</p>
        <p class="text-4xl font-bold text-green-400">{{ unifiedStats.deliveryRate }}%</p>
        <p class="text-xs text-gray-400 mt-2">{{ unifiedStats.totalDelivered }} of {{ unifiedStats.totalSent }} customers</p>
      </div>
      
      <!-- Email Engagement -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Email Open Rate</p>
        <p class="text-4xl font-bold text-blue-400">{{ emailStats.openRate }}%</p>
        <p class="text-xs text-gray-400 mt-2">{{ emailStats.opened }} customers opened</p>
      </div>
      
      <!-- SMS Success -->
      <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <p class="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">SMS Delivered</p>
        <p class="text-4xl font-bold text-emerald-400">{{ smsStats.deliveryRate }}%</p>
        <p class="text-xs text-gray-400 mt-2">{{ smsStats.delivered }} of {{ smsStats.total }} SMS</p>
      </div>
    </div>
    
    <!-- Progress Indicators -->
    <div v-if="(syncProgress.email.total > 0 && (isSyncing || isSyncingMissing)) || syncProgress.sms.total > 0" class="mb-4">
      <!-- Email Progress -->
      <div v-if="syncProgress.email.total > 0 && (isSyncing || isSyncingMissing)" class="bg-black/60 backdrop-blur-lg border border-blue-400/50 rounded-2xl p-4 mb-3 shadow-[0_8px_32px_rgba(59,130,246,0.3)]">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-blue-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            <span class="text-sm font-medium text-white">Syncing Emails...</span>
          </div>
          <span class="text-xs text-blue-300 font-semibold">
            Batch {{ syncProgress.email.current }} of {{ syncProgress.email.total }}
          </span>
        </div>
        <div class="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
          <div 
            class="bg-gradient-to-r from-blue-500 to-blue-600 h-2 transition-all duration-300"
            :style="{ width: `${syncProgress.email.percentage}%` }"
          ></div>
        </div>
        <p class="text-xs text-gray-400 mt-2">
          Processing large dataset... Data will appear progressively.
        </p>
      </div>
      
      <!-- SMS Progress -->
      <div v-if="syncProgress.sms.current > 0" class="bg-black/60 backdrop-blur-lg border border-emerald-400/50 rounded-2xl p-4 shadow-[0_8px_32px_rgba(16,185,129,0.3)]">
        <div class="flex items-center justify-between mb-2">
          <div class="flex items-center gap-2">
            <svg class="w-5 h-5 text-emerald-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
            </svg>
            <span class="text-sm font-medium text-white">Syncing SMS...</span>
          </div>
          <span class="text-xs text-emerald-300 font-semibold">
            Batch {{ syncProgress.sms.current }}
          </span>
        </div>
        <div class="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
          <div class="bg-gradient-to-r from-emerald-500 to-teal-500 h-2 animate-pulse"></div>
        </div>
        <p class="text-xs text-gray-400 mt-2">
          Fetching SMS data from Twilio... This may take a few moments.
        </p>
      </div>
    </div>
    
    <!-- Unified Communication Table - Apple Glass Style -->
    <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
      <!-- Table Header with Controls -->
      <div class="px-6 py-4 border-b border-white/10">
        <div class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div class="flex-shrink-0">
            <h3 class="text-lg font-bold text-white">Communication Tracking</h3>
            <p class="text-sm text-gray-300 mt-1">
              <span v-if="unifiedCommunications.length === 0 && isLoadingSMS" class="text-blue-400">
                🔄 Loading cached tracking data...
              </span>
              <span v-else-if="unifiedCommunications.length === 0 && isBackfillingSMS" class="text-emerald-400">
                🔄 Syncing SMS from Twilio...
              </span>
              <span v-else-if="unifiedCommunications.length === 0 && !isLoadingSMS && !isBackfillingSMS" class="text-yellow-400">
                ⚠️ No tracking data found - Use sync buttons to refresh
              </span>
              <span v-else-if="trackingData.length === 0 && smsTrackingData.length > 0" class="text-yellow-400">
                Showing {{ smsTrackingData.length }} SMS - Click "Sync Emails" to load email tracking
              </span>
              <span v-else-if="smsTrackingData.length === 0 && trackingData.length > 0" class="text-yellow-400">
                Showing {{ trackingData.length }} emails - SMS data loading automatically...
              </span>
              <span v-else>
                Monitor delivery and engagement across all channels
              </span>
            </p>
          </div>
          
          <!-- Search Bar (inline, between title and buttons) -->
          <div v-if="unifiedCommunications.length > 0" class="w-full lg:flex-1 lg:max-w-md lg:mx-4">
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by name, email, or phone..."
                class="search-input pl-12 pr-4 py-2.5 border border-white/20 rounded-full w-full backdrop-blur-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-400 focus:border-blue-400/50 transition-all duration-200 shadow-lg text-sm"
              />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex flex-wrap gap-2 flex-shrink-0">
            <!-- Email Sync Button -->
            <button
              @click="syncEmailTracking"
              :disabled="isSyncing || isLoading"
              class="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              :class="{ 'animate-pulse ring-2 ring-blue-400 ring-offset-2 ring-offset-black/60': trackingData.length === 0 && !isSyncing }"
              title="Sync email tracking data from SendGrid"
            >
              <svg
                class="w-4 h-4"
                :class="{ 'animate-spin': isSyncing }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              {{ isSyncing ? 'Syncing...' : 'Sync Emails' }}
            </button>
            
            <!-- SMS Backfill Button -->
            <button
              @click="backfillSMSTracking"
              :disabled="isBackfillingSMS || isLoadingSMS"
              class="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-full transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              title="Fetch SMS tracking data from Twilio (last 30 days)"
            >
              <svg
                class="w-4 h-4"
                :class="{ 'animate-spin': isBackfillingSMS }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
              </svg>
              {{ isBackfillingSMS ? 'Syncing...' : 'Sync SMS' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Warning Banner: Email Data Missing -->
      <div 
        v-if="smsTrackingData.length > 0 && trackingData.length === 0"
        class="px-6 py-3 bg-yellow-500/20 border-b border-yellow-500/30 flex items-start gap-3"
      >
        <svg class="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        <div class="flex-1">
          <p class="text-sm font-medium text-yellow-300">Email tracking data not loaded</p>
          <p class="text-xs text-yellow-400 mt-1">Click the "Sync Emails" button above to load email tracking data from SendGrid.</p>
        </div>
      </div>

      <!-- Unified Table -->
      <div v-if="filteredCommunications.length > 0" class="relative">
        <!-- Mobile Scroll Hint -->
        <div class="sm:hidden flex items-center justify-center py-2 text-xs text-gray-400">
          <svg class="w-4 h-4 mr-1 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16l-4-4m0 0l4-4m-4 4h18"/>
          </svg>
          Swipe to see more
          <svg class="w-4 h-4 ml-1 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
          </svg>
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <table class="min-w-full w-full">
              <thead>
                <tr class="bg-white/5">
                  <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">Customer</th>
                  <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">Email Status</th>
                  <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">SMS Status</th>
                  <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider whitespace-nowrap">Last Activity</th>
                </tr>
              </thead>
          <tbody class="divide-y divide-white/5">
            <tr 
              v-for="(customer, index) in paginatedCommunications" 
              :key="`customer-${index}`"
              class="hover:bg-white/5 transition-colors"
            >
              <!-- Customer Info -->
              <td class="px-3 sm:px-6 py-4">
                <div class="flex items-center gap-2 sm:gap-3 min-w-[200px]">
                  <!-- Customer Photo -->
                  <div class="flex-shrink-0">
                    <div
                      v-if="customer.photo"
                      class="h-10 w-10 rounded-full overflow-hidden ring-2 ring-white/20 bg-gray-700 cursor-pointer hover:ring-blue-400 transition-all"
                      @click="openImageModal(customer.photo, customer.contactName)"
                    >
                      <img
                        :src="customer.photo"
                        :alt="customer.contactName"
                        class="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div v-else class="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center ring-2 ring-white/20">
                      <span class="text-white text-sm font-semibold">
                        {{ (customer.contactName || 'U').charAt(0).toUpperCase() }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- Customer Details -->
                  <div class="flex flex-col min-w-0">
                    <span class="text-base font-medium text-white truncate">{{ customer.contactName || 'Unknown' }}</span>
                    <span v-if="customer.email" class="text-xs text-gray-400 truncate">{{ customer.email }}</span>
                    <span v-if="customer.phone" class="text-xs text-gray-500 truncate">{{ customer.phone }}</span>
                  </div>
                </div>
              </td>
              
              <!-- Email Status -->
              <td class="px-3 sm:px-6 py-4 whitespace-nowrap">
                <EmailStatusBadge 
                  v-if="customer.emailData" 
                  :tracking="customer.emailData"
                  @resend="handleResendEmail(customer)"
                />
                <!-- Email pending sync -->
                <span v-else-if="customer.email" class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 whitespace-nowrap" title="Email sent but tracking not synced yet - click 'Sync Emails' to load status">
                  <svg class="w-3.5 h-3.5 mr-1.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  Pending
                </span>
                <span v-else class="inline-flex items-center px-2 py-1 rounded text-xs text-gray-500 bg-gray-800/30 border border-gray-700/30" title="No email address for this customer">
                  No Email
                </span>
              </td>
              
              <!-- SMS Status -->
              <td class="px-3 sm:px-6 py-4 whitespace-nowrap">
                <SMSStatusBadge 
                  v-if="customer.smsData" 
                  :tracking="customer.smsData" 
                  @click="showSMSDetail(customer.smsData)"
                />
                <span v-else class="inline-flex items-center px-2 py-1 rounded text-xs text-gray-500 bg-gray-800/30 border border-gray-700/30" title="No SMS sent to this customer">
                  No SMS
                </span>
              </td>
              
              <!-- Last Activity -->
              <td class="px-3 sm:px-6 py-4 text-sm text-gray-300 whitespace-nowrap">
                {{ formatDate(customer.sentAt || customer.submissionDate) }}
              </td>
            </tr>
          </tbody>
        </table>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="px-6 py-12 text-center">
        <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
        </svg>
        <h3 class="text-lg font-semibold text-white mb-2">No Communication Data</h3>
        <p class="text-gray-300 mb-4">
          Click "Sync Emails" or "Sync SMS" buttons above to load tracking data
        </p>
      </div>

      <!-- Pagination -->
      <div v-if="filteredCommunications.length > 0" class="px-3 sm:px-6 py-4 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0">
        <div class="text-xs sm:text-sm text-gray-300 text-center sm:text-left">
          Showing {{ ((currentPage - 1) * entriesPerPage) + 1 }} to {{ Math.min(currentPage * entriesPerPage, filteredCommunications.length) }} of {{ filteredCommunications.length }}
        </div>
        <div class="flex items-center gap-2">
          <button
            @click="prevPage"
            :disabled="currentPage <= 1"
            class="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all bg-black/40 hover:bg-black/60 text-white border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span class="hidden sm:inline">Previous</span>
            <span class="sm:hidden">Prev</span>
          </button>
          <span class="text-xs sm:text-sm text-gray-300 px-2">
            {{ currentPage }} / {{ totalPages }}
          </span>
          <button
            @click="nextPage"
            :disabled="currentPage >= totalPages"
            class="px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all bg-black/40 hover:bg-black/60 text-white border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </div>

    <!-- SMS Detail Modal -->
    <SMSDetailModal
      :isOpen="showSMSModal"
      :sms="selectedSMS"
      @close="closeSMSModal"
    />

    <!-- Resend Email Modal -->
    <ResendEmailModal
      :isOpen="showResendModal"
      :tracking="resendData?.emailData"
      :customerName="resendData?.contactName"
      :formId="formId"
      :entryId="resendData?.entryId"
      @close="closeResendModal"
      @resent="handleEmailResent"
    />

    <!-- Image Modal -->
    <div
      v-if="showImageModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      @click="closeImageModal"
    >
      <div class="relative max-w-4xl w-full max-h-[90vh] mx-4" @click.stop>
        <!-- Modal header -->
        <div
          class="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4"
        >
          <h3 class="text-xl sm:text-2xl font-bold text-white bg-black/90 backdrop-blur-md px-5 py-3 rounded-2xl shadow-2xl border border-white/30">
            {{ selectedImageName }}
          </h3>
          <button
            @click="closeImageModal"
            class="p-3 rounded-full bg-black/90 text-white hover:bg-red-600 transition-all duration-200 shadow-2xl backdrop-blur-md border-2 border-white/30 hover:border-red-400"
          >
            <svg
              class="w-7 h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              stroke-width="3"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Modal image container -->
        <div class="w-full">
          <img
            :src="selectedImage"
            class="w-full h-auto object-contain max-h-[85vh] rounded-3xl border border-white/20 shadow-2xl"
            alt="Customer Photo"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EmailStatusBadge from './EmailStatusBadge.vue';
import SMSStatusBadge from './SMSStatusBadge.vue';
import SMSDetailModal from './SMSDetailModal.vue';
import ResendEmailModal from './ResendEmailModal.vue';

export default {
  name: 'CommunicationTracking',
  components: {
    EmailStatusBadge,
    SMSStatusBadge,
    SMSDetailModal,
    ResendEmailModal
  },
  props: {
    accountSlug: {
      type: String,
      required: true
    },
    submissions: {
      type: Array,
      default: () => []
    },
    formId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isSyncing: false,
      isSyncingMissing: false, // Track if we're auto-syncing missing emails
      initialLoadComplete: false, // Track if initial load is complete (prevents premature auto-sync)
      isLoadingSMS: false,
      isBackfillingSMS: false,
      error: null,
      trackingData: [],
      smsTrackingData: [],
      searchQuery: '',
      debouncedSearchQuery: '', // Debounced version for actual filtering
      searchDebounceTimer: null, // Timer for search debouncing
      currentPage: 1,
      entriesPerPage: 20,
      // SMS Modal
      showSMSModal: false,
      selectedSMS: {},
      // Image Modal
      showImageModal: false,
      selectedImage: '',
      selectedImageName: '',
      // Resend Email Modal
      showResendModal: false,
      resendData: null,
      // Progress tracking for batched sync
      syncProgress: {
        email: { current: 0, total: 0, percentage: 0 },
        sms: { current: 0, total: 0, percentage: 0 }
      },
      // Prevent infinite sync loops
      lastSyncTime: 0,
      syncCooldown: 10000, // 10 seconds cooldown between auto-syncs
      autoSyncTimeout: null, // Debounce timer for auto-sync
      stuckProgressInterval: null // Interval to check for stuck progress indicators
    };
  },
  computed: {
    // Email Statistics
    emailStats() {
      if (!this.trackingData || this.trackingData.length === 0) {
        return {
          sent: 0,
          delivered: 0,
          opened: 0,
          clicked: 0,
          bounced: 0,
          deliveryRate: 0,
          openRate: 0,
          clickRate: 0
        };
      }
      
      const sent = this.trackingData.length;
      const delivered = this.trackingData.filter(t => t.delivered).length;
      const opened = this.trackingData.filter(t => t.opened).length;
      const clicked = this.trackingData.filter(t => t.clicked).length;
      const bounced = this.trackingData.filter(t => t.bounced).length;
      
      return {
        sent,
        delivered,
        opened,
        clicked,
        bounced,
        deliveryRate: sent > 0 ? ((delivered / sent) * 100).toFixed(1) : 0,
        openRate: delivered > 0 ? ((opened / delivered) * 100).toFixed(1) : 0,
        clickRate: opened > 0 ? ((clicked / opened) * 100).toFixed(1) : 0
      };
    },
    
    // SMS Statistics
    smsStats() {
      if (!this.smsTrackingData || this.smsTrackingData.length === 0) {
        return {
          total: 0,
          delivered: 0,
          failed: 0,
          deliveryRate: 0
        };
      }
      
      const total = this.smsTrackingData.length;
      const delivered = this.smsTrackingData.filter(sms => sms.delivered).length;
      const failed = this.smsTrackingData.filter(sms => sms.status === 'failed' || sms.status === 'undelivered').length;
      
      return {
        total,
        delivered,
        failed,
        deliveryRate: total > 0 ? ((delivered / total) * 100).toFixed(1) : 0
      };
    },
    
    // Communication Statistics - based on unified communications (submissions)
    communicationStats() {
      const customers = this.unifiedCommunications;
      const totalCustomers = customers.length;

      // Count customers with email/SMS (from submission data, not tracking)
      const withEmail = customers.filter(c => c.email).length;
      const withSMS = customers.filter(c => c.phone).length;

      return {
        totalCustomers,
        withEmail,
        withSMS
      };
    },

    // Unified Statistics
    unifiedStats() {
      // Total sent = unique customers (not sum of channels)
      const totalSent = this.unifiedCommunications.length;

      // Total delivered = customers who had at least one channel delivered
      const totalDelivered = this.unifiedCommunications.filter(customer => {
        const emailDelivered = customer.emailData && customer.emailData.delivered;
        const smsDelivered = customer.smsData && customer.smsData.status === 'delivered';
        return emailDelivered || smsDelivered;
      }).length;

      const deliveryRate = totalSent > 0 ? ((totalDelivered / totalSent) * 100).toFixed(1) : 0;

      return {
        totalSent,
        totalDelivered,
        deliveryRate
      };
    },
    
    // Grouped Communications by Customer (last 30 days only)
    // OPTIMIZED: Pre-builds lookup maps for O(1) access instead of O(n) loops
    unifiedCommunications() {
      const customerMap = new Map();

      // Calculate 30 days ago threshold
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoTime = thirtyDaysAgo.getTime();

      // OPTIMIZATION: Pre-build lookup maps for O(1) access instead of O(n) loops
      // Build email tracking lookup map
      const emailTrackingMap = new Map();
      this.trackingData.forEach(emailTracking => {
        const trackingEmail = emailTracking.email || emailTracking.to_email || emailTracking.recipient;
        if (trackingEmail) {
          emailTrackingMap.set(trackingEmail.toLowerCase().trim(), emailTracking);
        }
      });

      // Build SMS tracking lookup map (by normalized phone)
      const smsTrackingMap = new Map();
      this.smsTrackingData.forEach(smsTracking => {
        if (smsTracking.phoneNumber) {
          const normalizedPhone = this.normalizePhone(smsTracking.phoneNumber);
          smsTrackingMap.set(normalizedPhone, smsTracking);
        }
      });

      // Process submissions to create customer entries (filter to last 30 days)
      const submissionsLength = this.submissions.length;
      for (let i = 0; i < submissionsLength; i++) {
        const submission = this.submissions[i];

        // Skip submissions older than 30 days
        const submissionDate = submission.date_created ? new Date(submission.date_created) : null;
        if (submissionDate && submissionDate.getTime() < thirtyDaysAgoTime) {
          continue;
        }

        const email = submission['2'];
        const phone = submission['8'];
        const name = submission['1'] || 'Unknown';
        const photo = submission['13'];
        const entryId = submission.id;

        if (!email && !phone) continue;

        const normalizedEmail = email ? email.toLowerCase().trim() : null;
        const key = normalizedEmail || phone;

        if (!customerMap.has(key)) {
          // OPTIMIZATION: Look up tracking data directly using O(1) map lookup
          const emailData = normalizedEmail ? emailTrackingMap.get(normalizedEmail) : null;
          const normalizedPhone = phone ? this.normalizePhone(phone) : null;
          const smsData = normalizedPhone ? smsTrackingMap.get(normalizedPhone) : null;

          // Calculate sentAt based on available data
          let sentAt = submission.date_created || null;

          if (emailData) {
            const emailDate = new Date(
              emailData.lastEventDate || emailData.last_event_time ||
              emailData.processed_time || emailData.date || 0
            );
            if (emailDate.getTime() > 0 && (!sentAt || emailDate > new Date(sentAt))) {
              sentAt = emailData.lastEventDate || emailData.last_event_time ||
                       emailData.processed_time || emailData.date;
            }
          }

          if (smsData) {
            const smsDate = new Date(smsData.lastEventDate || 0);
            if (smsDate.getTime() > 0 && (!sentAt || smsDate > new Date(sentAt))) {
              sentAt = smsData.lastEventDate;
            }
          }

          customerMap.set(key, {
            contactName: name,
            email: email,
            phone: phone,
            photo: photo,
            entryId: entryId,
            emailData: emailData,
            smsData: smsData,
            sentAt: sentAt,
            submissionDate: submission.date_created || null
          });
        } else {
          const existing = customerMap.get(key);
          const subDate = submission.date_created;
          if (subDate && (!existing.sentAt || new Date(subDate) > new Date(existing.sentAt || 0))) {
            existing.sentAt = subDate;
          }
        }
      }

      // Convert map to array
      const communications = Array.from(customerMap.values());

      // Sort by most recent communication
      communications.sort((a, b) => {
        const dateA = new Date(a.sentAt || a.submissionDate || 0);
        const dateB = new Date(b.sentAt || b.submissionDate || 0);
        return dateB - dateA;
      });

      return communications;
    },
    
    // Filtered and Searched Communications
    // OPTIMIZED: Uses debounced search query to avoid filtering on every keystroke
    filteredCommunications() {
      const communications = this.unifiedCommunications;

      // Use debounced search query for filtering (updated 300ms after typing stops)
      const query = this.debouncedSearchQuery;
      if (!query) {
        return communications;
      }

      const lowerQuery = query.toLowerCase();
      return communications.filter(customer => {
        const name = customer.contactName || '';
        const email = customer.email || '';
        const phone = customer.phone || '';

        return name.toLowerCase().includes(lowerQuery) ||
               email.toLowerCase().includes(lowerQuery) ||
               phone.toLowerCase().includes(lowerQuery);
      });
    },
    
    // Pagination
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredCommunications.length / this.entriesPerPage));
    },
    
    paginatedCommunications() {
      const start = (this.currentPage - 1) * this.entriesPerPage;
      const end = start + this.entriesPerPage;
      return this.filteredCommunications.slice(start, end);
    },
    
    // Emails without tracking data (for auto-sync)
    emailsWithoutTracking() {
      if (!this.submissions || this.submissions.length === 0) {
        return [];
      }
      
      // Get all emails from submissions
      const allEmails = this.submissions
        .map(s => s['2'])
        .filter(email => email && email.includes('@'));
      
      if (allEmails.length === 0) {
        return [];
      }
      
      // Create a set of emails that have tracking data
      const emailsWithTracking = new Set(
        this.trackingData
          .map(t => {
            const email = t.email || t.to_email || t.recipient;
            return email ? email.toLowerCase().trim() : null;
          })
          .filter(e => e)
      );
      
      // Return emails that don't have tracking
      return allEmails.filter(email => {
        const normalizedEmail = email.toLowerCase().trim();
        return !emailsWithTracking.has(normalizedEmail);
      });
    }
  },
  watch: {
    submissions: {
      async handler(newVal) {
        // Load tracking data when submissions become available
        if (newVal && newVal.length > 0) {
          // Sync email first, then SMS when submissions change
          await this.fetchEmailTracking();
          await this.fetchSMSTracking();
        }
      },
      immediate: true
    },
    searchQuery(newVal) {
      // Debounce search to avoid filtering on every keystroke
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchQuery = newVal.trim();
        // Reset to page 1 when search changes
        this.currentPage = 1;
      }, 300); // 300ms debounce
    },
    // Auto-sync emails without tracking data
    async emailsWithoutTracking(newVal, oldVal) {
      // Clear any pending timeout
      if (this.autoSyncTimeout) {
        clearTimeout(this.autoSyncTimeout);
        this.autoSyncTimeout = null;
      }
      
      // If all emails now have tracking, clear any stuck progress indicators
      if (newVal.length === 0) {
        this.clearStuckSyncProgress();
        return;
      }
      
      // Only auto-sync if:
      // 1. There are emails without tracking
      // 2. We're not already syncing (manual or auto)
      // 3. We're not currently loading
      // 4. We have submissions loaded
      // 5. Initial load is complete (prevents premature auto-sync on mount)
      // 6. Cooldown period has passed since last sync
      // 7. The value actually changed (not just reactive update)
      const now = Date.now();
      const cooldownPassed = (now - this.lastSyncTime) >= this.syncCooldown;
      const valueChanged = JSON.stringify(newVal) !== JSON.stringify(oldVal);
      
      if (
        newVal.length > 0 && 
        !this.isSyncing && 
        !this.isSyncingMissing && 
        !this.isLoading &&
        this.submissions.length > 0 &&
        this.initialLoadComplete &&
        cooldownPassed &&
        valueChanged
      ) {
        // Debounce: Wait 2 seconds before triggering sync to avoid rapid re-triggers
        this.autoSyncTimeout = setTimeout(async () => {
          // Double-check conditions haven't changed
          if (
            !this.isSyncing && 
            !this.isSyncingMissing && 
            !this.isLoading &&
            (Date.now() - this.lastSyncTime) >= this.syncCooldown
          ) {
            this.lastSyncTime = Date.now();
            await this.syncMissingEmailTracking(newVal);
          }
        }, 2000);
      }
    }
  },
  mounted() {
    // Set up periodic check for stuck sync progress (every 5 seconds)
    this.stuckProgressInterval = setInterval(() => {
      this.clearStuckSyncProgress();
    }, 5000);
  },
  beforeDestroy() {
    // Clean up timeout on component destroy
    if (this.autoSyncTimeout) {
      clearTimeout(this.autoSyncTimeout);
      this.autoSyncTimeout = null;
    }
    // Clean up search debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    // Clean up interval
    if (this.stuckProgressInterval) {
      clearInterval(this.stuckProgressInterval);
      this.stuckProgressInterval = null;
    }
  },
  methods: {
    getContactName(email) {
      // Try to find the contact name from submissions
      const submission = this.submissions.find(s => s['2'] === email);
      if (submission && submission['1']) {
        return submission['1'];
      }
      // Fallback to email username
      return email ? email.split('@')[0] : 'Unknown';
    },
    
    getContactNameByPhone(phoneNumber) {
      // Try to find the contact name from submissions
      const submission = this.submissions.find(s => {
        const submissionPhone = s['8'];
        return submissionPhone && this.normalizePhone(submissionPhone) === this.normalizePhone(phoneNumber);
      });
      if (submission && submission['1']) {
        return submission['1'];
      }
      return 'Unknown';
    },
    
    normalizePhone(phone) {
      if (!phone) return '';
      // Remove all non-digit characters and normalize
      const digits = phone.replace(/\D/g, '');
      // Convert Australian local format to international
      if (digits.startsWith('04') && digits.length === 10) {
        return '+614' + digits.substring(2);
      }
      if (digits.startsWith('614')) {
        return '+' + digits;
      }
      return phone;
    },
    
    async fetchEmailTracking(skipAutoSync = false) {
      if (!this.accountSlug || this.submissions.length === 0) {
        return;
      }
      
      this.isLoading = true;
      this.error = null;
      
      try {
        const allEmails = this.submissions
          .map(s => s['2'])
          .filter(email => email && email.includes('@'));
        
        if (allEmails.length === 0) {
          this.trackingData = [];
          return;
        }
        
        const response = await axios.post('/.netlify/functions/sync-email-tracking', {
          formId: this.formId,
          emails: allEmails,
          loadOnly: true
        });
        
        // Handle both array and object response formats
        let trackingArray = [];
        if (Array.isArray(response.data.tracking)) {
          trackingArray = response.data.tracking;
        } else if (response.data.tracking && typeof response.data.tracking === 'object') {
          // Convert object to array (keys are emails)
          trackingArray = Object.values(response.data.tracking);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          trackingArray = response.data.data;
        }
        
        this.trackingData = trackingArray;
        
        // Check for missing emails and trigger sync if needed (skip if called from syncMissingEmailTracking)
        if (!skipAutoSync) {
          const emailsWithTracking = new Set(
            trackingArray.map(t => {
              const email = t.email || t.to_email || t.recipient;
              return email ? email.toLowerCase().trim() : null;
            }).filter(e => e)
          );
          
          const missingEmails = allEmails.filter(email => {
            const normalizedEmail = email.toLowerCase().trim();
            return !emailsWithTracking.has(normalizedEmail);
          });
          
          // If there are missing emails and we're not already syncing, trigger sync for missing ones
          // Note: The emailsWithoutTracking watcher also handles auto-sync with debouncing,
          // so we only trigger here if cooldown has passed to avoid duplicate calls
          const cooldownPassed = (Date.now() - this.lastSyncTime) >= this.syncCooldown;
          if (missingEmails.length > 0 && !this.isSyncing && !this.isSyncingMissing && this.initialLoadComplete && cooldownPassed) {
            // Trigger sync for missing emails in background (non-blocking)
            this.syncMissingEmailTracking(missingEmails).catch(() => {
              // Error syncing missing emails
            });
          }
        }
        
        // Cache will be invalidated by the trackingData watcher
        
      } catch (error) {
        this.error = 'Click "Sync Emails" to load tracking information';
        this.trackingData = [];
      } finally {
        this.isLoading = false;
        // Mark initial load as complete after first fetch
        if (!this.initialLoadComplete) {
          this.initialLoadComplete = true;
        }
      }
    },
    
    async syncMissingEmailTracking(missingEmails) {
      if (!this.formId || !missingEmails || missingEmails.length === 0) {
        return;
      }
      
      // Prevent rapid re-syncing
      const now = Date.now();
      if ((now - this.lastSyncTime) < this.syncCooldown) {
        return;
      }
      
      this.isSyncingMissing = true;
      this.lastSyncTime = Date.now();
      
      try {
        const formId = this.formId;
        const uniqueEmails = [...new Set(missingEmails)];
        
        // Use progressive batching if needed
        const BATCH_SIZE = 50;
        const needsBatching = uniqueEmails.length > BATCH_SIZE;
        
        if (!needsBatching) {
          // Small dataset: Single batch
          const response = await axios.post('/.netlify/functions/sync-email-tracking', {
            formId: formId,
            emails: uniqueEmails
          });
          
          // Merge new tracking data directly (faster than reloading)
          if (response.data.tracking && Array.isArray(response.data.tracking) && response.data.tracking.length > 0) {
            // Create a map of existing tracking data by normalized email
            const trackingMap = new Map();
            this.trackingData.forEach(t => {
              const email = t.email || t.to_email || t.recipient;
              if (email) {
                trackingMap.set(email.toLowerCase().trim(), t);
              }
            });
            
            // Merge new tracking data (newer data overwrites older)
            response.data.tracking.forEach(tracking => {
              const email = tracking.email || tracking.to_email || tracking.recipient;
              if (email) {
                const normalizedEmail = email.toLowerCase().trim();
                const existing = trackingMap.get(normalizedEmail);
                if (!existing) {
                  trackingMap.set(normalizedEmail, tracking);
                } else {
                  // Update existing if new data is more recent
                  const existingDate = new Date(existing.lastEventDate || existing.last_event_time || existing.processed_time || 0);
                  const newDate = new Date(tracking.lastEventDate || tracking.last_event_time || tracking.processed_time || 0);
                  if (newDate > existingDate) {
                    trackingMap.set(normalizedEmail, tracking);
                  }
                }
              }
            });
            
            // Update tracking data (cache invalidated by watcher)
            this.trackingData = Array.from(trackingMap.values());
          } else {
            // Fallback: reload from cache if no tracking data in response (skip auto-sync to avoid loops)
            await this.fetchEmailTracking(true);
          }
        } else {
          // Large dataset: Progressive batching
          // IMPORTANT: Pass ALL emails to sync function - it handles batching internally
          const totalBatches = Math.ceil(uniqueEmails.length / BATCH_SIZE);
          
          // Initialize progress indicator for auto-sync
          this.syncProgress.email = {
            current: 0,
            total: totalBatches,
            percentage: 0
          };
          
          for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
            try {
              // Update progress
              this.syncProgress.email = {
                current: batchIndex + 1,
                total: totalBatches,
                percentage: Math.round(((batchIndex + 1) / totalBatches) * 100)
              };
              
              // Pass ALL emails - the sync function will slice them based on batchIndex
              const response = await axios.post('/.netlify/functions/sync-email-tracking', {
                formId: formId,
                emails: uniqueEmails, // Pass all emails, not just batch
                batchIndex: batchIndex,
                batchSize: BATCH_SIZE
              });
              
              if (response.data && response.data.success) {
                // Merge new tracking data directly (faster than reloading)
                if (response.data.tracking && Array.isArray(response.data.tracking) && response.data.tracking.length > 0) {
                  // Create a map of existing tracking data by normalized email
                  const trackingMap = new Map();
                  this.trackingData.forEach(t => {
                    const email = t.email || t.to_email || t.recipient;
                    if (email) {
                      trackingMap.set(email.toLowerCase().trim(), t);
                    }
                  });
                  
                  // Merge new tracking data (newer data overwrites older)
                  // Response now contains ALL tracking data, not just batch
                  response.data.tracking.forEach(tracking => {
                    const email = tracking.email || tracking.to_email || tracking.recipient;
                    if (email) {
                      const normalizedEmail = email.toLowerCase().trim();
                      const existing = trackingMap.get(normalizedEmail);
                      if (!existing) {
                        trackingMap.set(normalizedEmail, tracking);
                      } else {
                        // Update existing if new data is more recent
                        const existingDate = new Date(existing.lastEventDate || existing.last_event_time || existing.processed_time || existing.date || 0);
                        const newDate = new Date(tracking.lastEventDate || tracking.last_event_time || tracking.processed_time || tracking.date || 0);
                        if (newDate > existingDate) {
                          trackingMap.set(normalizedEmail, tracking);
                        }
                      }
                    }
                  });
                  
                  // Update tracking data with merged result (cache invalidated by watcher)
                  this.trackingData = Array.from(trackingMap.values());
                } else {
                  // Fallback: reload from cache if no tracking data in response (skip auto-sync to avoid loops)
                  await this.fetchEmailTracking(true);
                }
              }

              // Rate limiting: Wait 2 seconds between batches
              if (batchIndex < totalBatches - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            } catch (error) {
              // Continue with next batch even if one fails
              // Still wait before next batch to avoid overwhelming the server
              if (batchIndex < totalBatches - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
            }
          }
        }
      } catch (error) {
        // Don't show error to user - this is background sync
      } finally {
        this.isSyncingMissing = false;
        // Clear progress indicator when done
        this.syncProgress.email = { current: 0, total: 0, percentage: 0 };
      }
    },
    
    async syncEmailTracking() {
      if (!this.formId || this.submissions.length === 0) {
        return;
      }
      
      // Prevent rapid re-syncing (manual syncs also respect cooldown)
      const now = Date.now();
      if ((now - this.lastSyncTime) < 2000) {
        return;
      }
      
      this.isSyncing = true;
      this.error = null;
      this.lastSyncTime = Date.now();
      
      try {
        const formId = this.formId;
        
        // Extract ALL unique emails from submissions
        const allEmails = [...new Set(
          this.submissions
            .map(s => s['2'])
            .filter(email => email && email.includes('@'))
        )];
        
        // Step 1: Load cached data immediately (fast initial load)
        await this.fetchEmailTracking();
        
        // Step 2: Determine if we need progressive batching
        const BATCH_SIZE = 50;
        const needsBatching = allEmails.length > BATCH_SIZE;
        
        if (!needsBatching) {
          // Small dataset: Use existing single-batch approach
          const response = await axios.post('/.netlify/functions/sync-email-tracking', {
            formId: formId,
            emails: allEmails
          });
          
          // If tracking data is in the response, use it directly (faster)
          // Cache will be invalidated by the trackingData watcher
          if (response.data.tracking && Array.isArray(response.data.tracking) && response.data.tracking.length > 0) {
            this.trackingData = response.data.tracking;
          } else {
            // Fallback: reload from Blobs
            await new Promise(resolve => setTimeout(resolve, 500));
            await this.fetchEmailTracking();
          }
          return;
        }
        
        // Step 3: Progressive batching for large datasets
        const totalBatches = Math.ceil(allEmails.length / BATCH_SIZE);
        
        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          // Update progress
          this.syncProgress.email = {
            current: batchIndex + 1,
            total: totalBatches,
            percentage: Math.round(((batchIndex + 1) / totalBatches) * 100)
          };
          
          const response = await axios.post('/.netlify/functions/sync-email-tracking', {
            formId: formId,
            emails: allEmails,
            batchIndex: batchIndex,
            batchSize: BATCH_SIZE
          });
          
          // If tracking data is in the response, use it directly (faster)
          if (response.data.tracking && Array.isArray(response.data.tracking) && response.data.tracking.length > 0) {
            // Create a map of existing tracking data by normalized email
            const trackingMap = new Map();
            this.trackingData.forEach(t => {
              const email = t.email || t.to_email || t.recipient;
              if (email) {
                trackingMap.set(email.toLowerCase().trim(), t);
              }
            });
            
            // Merge new tracking data (newer data overwrites older)
            // Response now contains ALL tracking data, not just batch
            response.data.tracking.forEach(tracking => {
              const email = tracking.email || tracking.to_email || tracking.recipient;
              if (email) {
                const normalizedEmail = email.toLowerCase().trim();
                const existing = trackingMap.get(normalizedEmail);
                if (!existing) {
                  trackingMap.set(normalizedEmail, tracking);
                } else {
                  // Update existing if new data is more recent
                  const existingDate = new Date(existing.lastEventDate || existing.last_event_time || existing.processed_time || existing.date || 0);
                  const newDate = new Date(tracking.lastEventDate || tracking.last_event_time || tracking.processed_time || tracking.date || 0);
                  if (newDate > existingDate) {
                    trackingMap.set(normalizedEmail, tracking);
                  }
                }
              }
            });
            
            // Convert map back to array (cache invalidated by watcher)
            this.trackingData = Array.from(trackingMap.values());
          } else {
            // Fallback: reload from Blobs
            await new Promise(resolve => setTimeout(resolve, 500));
            await this.fetchEmailTracking();
          }

          // Rate limiting: Wait 2 seconds between batches to avoid API limits
          if (batchIndex < totalBatches - 1) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }

        // Final refresh to ensure all data is loaded
        await new Promise(resolve => setTimeout(resolve, 500));
        await this.fetchEmailTracking();

      } catch (error) {
        this.error = error.response?.data?.error || 'Failed to sync email tracking data';
      } finally {
        this.isSyncing = false;
        this.syncProgress.email = { current: 0, total: 0, percentage: 0 };
      }
    },
    
    async fetchSMSTracking() {
      if (!this.formId) {
        return;
      }

      this.isLoadingSMS = true;

      try {
        const response = await axios.post('/.netlify/functions/sync-sms-tracking', {
          formId: this.formId
        });

        if (response.data.success && response.data.data) {
          this.smsTrackingData = Object.values(response.data.data);
        } else {
          this.smsTrackingData = [];
        }
      } catch (error) {
        this.smsTrackingData = [];
      } finally {
        this.isLoadingSMS = false;
      }
    },
    
    async backfillSMSTracking() {
      if (!this.formId || this.isBackfillingSMS) {
        return;
      }
      
      this.isBackfillingSMS = true;
      this.error = null;
      
      try {
        // Step 1: Load cached data immediately (fast initial load)
        await this.fetchSMSTracking();
        
        // Step 2: Start progressive batching
        const BATCH_SIZE = 25; // Smaller batches for SMS (API is slower)
        // No limit on submissions - process all (backfill function will handle batching)
        const SUBMISSIONS_LIMIT = 1000; // Increased from 100 to handle more submissions
        
        let batchIndex = 0;
        let hasMore = true;
        let totalPhonesProcessed = 0;
        let totalEventsStored = 0;
        
        while (hasMore) {
          // Update progress (we don't know total in advance, so show current batch)
          this.syncProgress.sms = {
            current: batchIndex + 1,
            total: 0, // Unknown total
            percentage: 0 // Can't calculate without knowing total
          };
          
          const response = await axios.post('/.netlify/functions/backfill-twilio-tracking', {
            formId: this.formId,
            limit: SUBMISSIONS_LIMIT,
            batchIndex: batchIndex,
            batchSize: BATCH_SIZE
          });
          
          if (response.data.success) {
            const stats = response.data.stats || {};
            const phonesProcessed = stats.phonesProcessed || 0;
            const eventsStored = stats.eventsStored || 0;
            
            totalPhonesProcessed += phonesProcessed;
            totalEventsStored += eventsStored;
            
            // Check if there are more batches
            hasMore = response.data.hasMore || false;
            batchIndex++;
            
            // Refresh UI with new data after each batch
            await this.fetchSMSTracking();
            
            // Rate limiting: Wait 3 seconds between batches
            if (hasMore) {
              await new Promise(resolve => setTimeout(resolve, 3000));
            }
          } else {
            hasMore = false;
          }
        }
        
        // Show summary message
        if (totalPhonesProcessed > 0 && totalEventsStored === 0) {
          alert(`SMS Sync Complete\n\nProcessed ${totalPhonesProcessed} phone numbers but no SMS messages found.\n\nThis could mean:\n• Customers haven't been sent SMS yet\n• SMS were sent more than 30 days ago\n• Phone numbers don't match Twilio records`);
        }
        
      } catch (error) {
        // Extract detailed error message
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error';
        
        // Show user-friendly error alert
        alert(`SMS Sync Failed\n\n${errorMessage}\n\nPlease check:\n• Twilio credentials are configured\n• Form has valid phone numbers\n• Network connection is stable\n\nCheck browser console for details.`);
        
        this.error = error.response?.data?.error || 'Failed to backfill SMS tracking';
      } finally {
        this.isBackfillingSMS = false;
        this.syncProgress.sms = { current: 0, total: 0, percentage: 0 };
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // Clear any stuck sync progress when navigating pages
        this.clearStuckSyncProgress();
      }
    },
    // Clear stuck sync progress (safety check)
    clearStuckSyncProgress() {
      // If sync flags are false but progress is still showing, clear it
      if (!this.isSyncing && !this.isSyncingMissing && this.syncProgress.email.total > 0) {
        this.syncProgress.email = { current: 0, total: 0, percentage: 0 };
      }
    },
    
    showSMSDetail(sms) {
      this.selectedSMS = sms;
      this.showSMSModal = true;
    },
    
    closeSMSModal() {
      this.showSMSModal = false;
    },
    
    openImageModal(imageUrl, customerName) {
      this.selectedImage = imageUrl;
      this.selectedImageName = customerName || 'Customer Photo';
      this.showImageModal = true;
    },
    
    closeImageModal() {
      this.showImageModal = false;
      this.selectedImage = '';
      this.selectedImageName = '';
    },
    
    handleResendEmail(customer) {
      // Open resend modal with customer data
      this.resendData = customer;
      this.showResendModal = true;
    },
    
    closeResendModal() {
      this.showResendModal = false;
    },
    
    async handleEmailResent() {
      // Handle successful email resend
      // Immediately refresh from cache to show "Resent" status badge
      await this.fetchEmailTracking();
      
      // Wait a moment for SendGrid to process the new email, then do full sync
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Refresh email tracking data to get updated status from SendGrid
      await this.syncEmailTracking();
    }
  }
};
</script>

<style scoped>
.communication-tracking-container {
  margin-top: 2rem;
}

/* Search input styling (matching TotalEntriesList.vue) */
.search-input {
  color: white !important;
  background: rgba(0, 0, 0, 0.4) !important;
  backdrop-filter: blur(16px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  appearance: none !important;
}

.search-input::placeholder {
  color: rgba(156, 163, 175, 0.8) !important;
  opacity: 1 !important;
}

.search-input:focus {
  color: white !important;
  background: rgba(0, 0, 0, 0.6) !important;
  border-color: rgba(59, 130, 246, 0.5) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4), 0 10px 25px rgba(0, 0, 0, 0.2) !important;
}

/* Override any global input styles */
input[type="text"].search-input {
  color: white !important;
  -webkit-text-fill-color: white !important;
  opacity: 1 !important;
  -webkit-opacity: 1 !important;
}

input[type="text"].search-input::placeholder {
  color: rgba(156, 163, 175, 0.8) !important;
  opacity: 1 !important;
}

/* Table scroll styling */
.overflow-x-auto {
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: rgba(59, 130, 246, 0.5) rgba(0, 0, 0, 0.2);
}

.overflow-x-auto::-webkit-scrollbar {
  height: 8px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: rgba(59, 130, 246, 0.5);
  border-radius: 4px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(59, 130, 246, 0.7);
}

/* Mobile specific fixes */
@media (max-width: 768px) {
  .search-input {
    font-size: 16px !important; /* Prevents zoom on iOS */
    background: rgba(0, 0, 0, 0.6) !important;
  }
  
  /* Ensure table scrolls smoothly on mobile */
  .overflow-x-auto {
    overflow-x: scroll;
    scroll-behavior: smooth;
  }
  
  /* Add padding to table container on mobile */
  table {
    min-width: 800px; /* Ensure table has minimum width for proper scrolling */
  }
}
</style>

